import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FaArrowLeft,
  FaCheckCircle,
  FaIndustry,
  FaTruck,
  FaWhatsapp,
  FaPaperPlane,
  FaChevronRight,
  FaChevronLeft,
  FaHome,
  FaSearchPlus,
  FaSearchMinus,
  FaTimes,
  FaRedo
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { getProductById, saveEnquiry } from '../firebase/db';
import {
  getLocalizedProductName,
  getLocalizedCategory,
  getLocalizedCapacity,
  getLocalizedOverview,
  getLocalizedDetailedSpecs,
  translateProductText,
  normalizeLang
} from '../utils/productLocalization';
import './ProductDetail.css';

const ProductDetail = () => {
  const { t, i18n } = useTranslation();
  const currentLang = normalizeLang(i18n.language || 'mr');
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImgIndex, setSelectedImgIndex] = useState(0);
  const [, setDynamicTrigger] = useState(0);

  // Lightbox & Zoom State
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Consultation / Quick Quote Form State
  const [quoteForm, setQuoteForm] = useState({ name: '', phone: '', email: '', message: '' });
  const [submittingQuote, setSubmittingQuote] = useState(false);

  useEffect(() => {
    const fetchProductData = async () => {
      setLoading(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      try {
        const data = await getProductById(id);
        setProduct(data);
        setSelectedImgIndex(0);
      } catch (err) {
        console.error('Error loading product:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProductData();
  }, [id]);

  const imagesList = product?.images && product.images.length > 0
    ? product.images
    : ['https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=800'];
  const activeImage = imagesList[selectedImgIndex] || imagesList[0];

  const handleZoomIn = (e) => {
    if (e) e.stopPropagation();
    setZoomLevel(prev => Math.min(Number((prev + 0.3).toFixed(2)), 3.5));
  };

  const handleZoomOut = (e) => {
    if (e) e.stopPropagation();
    setZoomLevel(prev => {
      const next = Math.max(Number((prev - 0.3).toFixed(2)), 1);
      if (next === 1) setPan({ x: 0, y: 0 });
      return next;
    });
  };

  const handleResetZoom = (e) => {
    if (e) e.stopPropagation();
    setZoomLevel(1);
    setPan({ x: 0, y: 0 });
  };

  const openLightbox = () => {
    setIsLightboxOpen(true);
    setZoomLevel(1);
    setPan({ x: 0, y: 0 });
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
    setZoomLevel(1);
    setPan({ x: 0, y: 0 });
    setIsDragging(false);
  };

  // Keyboard controls and body scroll locking
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isLightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === '+' || e.key === '=') handleZoomIn();
      if (e.key === '-' || e.key === '_') handleZoomOut();
      if (e.key === '0') handleResetZoom();
      if (e.key === 'ArrowRight' && imagesList.length > 1) {
        setSelectedImgIndex(prev => (prev + 1) % imagesList.length);
        handleResetZoom();
      }
      if (e.key === 'ArrowLeft' && imagesList.length > 1) {
        setSelectedImgIndex(prev => (prev - 1 + imagesList.length) % imagesList.length);
        handleResetZoom();
      }
    };
    if (isLightboxOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isLightboxOpen, imagesList.length]);

  const handleMouseDown = (e) => {
    if (zoomLevel > 1) {
      e.preventDefault();
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoomLevel > 1) {
      e.preventDefault();
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    if (zoomLevel > 1 && e.touches.length === 1) {
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - pan.x,
        y: e.touches[0].clientY - pan.y
      });
    }
  };

  const handleTouchMove = (e) => {
    if (isDragging && zoomLevel > 1 && e.touches.length === 1) {
      setPan({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y
      });
    }
  };

  const handleWheel = (e) => {
    if (!isLightboxOpen) return;
    if (e.deltaY < 0) {
      handleZoomIn();
    } else {
      handleZoomOut();
    }
  };

  // Background on-demand dynamic translation for dynamic products lacking translation
  useEffect(() => {
    if (!product || currentLang === 'en') return;

    let isMounted = true;
    const loadDynamicTranslations = async () => {
      let updated = false;
      const nameField = currentLang === 'mr' ? (product.name_mr || product.nameMr) : (product.name_kn || product.nameKn);
      const descField = currentLang === 'mr' ? (product.fullDescription_mr || product.fullDescriptionMr || product.overview_mr) : (product.fullDescription_kn || product.fullDescriptionKn || product.overview_kn);

      if (!nameField && product.name) {
        await translateProductText(product.name, currentLang);
        updated = true;
      }
      if (!descField && (product.fullDescription || product.overview || product.shortDescription)) {
        await translateProductText(product.fullDescription || product.overview || product.shortDescription, currentLang);
        updated = true;
      }
      if (updated && isMounted) {
        setDynamicTrigger(prev => prev + 1);
      }
    };
    loadDynamicTranslations();

    return () => { isMounted = false; };
  }, [product, currentLang]);

  const displayName = getLocalizedProductName(product, currentLang);
  const displayCategory = getLocalizedCategory(product?.category, t, currentLang, product);
  const capacityText = getLocalizedCapacity(product?.capacity, currentLang, product);
  const overviewContent = getLocalizedOverview(product, t, currentLang);
  const detailedSpecs = getLocalizedDetailedSpecs(product, t, currentLang);

  const getWhatsAppEnquiryUrl = () => {
    if (!product) return 'https://wa.me/918956403388';

    const pageUrl = typeof window !== 'undefined' ? window.location.href : '';
    const textLines = [
      `Hello Digvijay Industries,`,
      ``,
      `I am interested in inquiring about your machinery:`,
      `*${displayName || product.name}*`,
      product.category ? `• Category: ${displayCategory || product.category}` : null,
      product.capacity ? `• Capacity: ${capacityText || product.capacity}` : null,
      ``,
      `Product Link: ${pageUrl}`,
      ``,
      `Please provide more details, price quotation, and consultation.`
    ].filter(line => line !== null).join('\n');

    return `https://wa.me/918956403388?text=${encodeURIComponent(textLines)}`;
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    if (!quoteForm.name || !quoteForm.phone) {
      Swal.fire({
        icon: 'warning',
        title: t('alertMissingTitle'),
        text: t('alertMissingText'),
        confirmButtonColor: '#04416d'
      });
      return;
    }

    setSubmittingQuote(true);
    const enquiryPayload = {
      ...quoteForm,
      type: 'product',
      enquiryType: 'product',
      subject: `Consultation & Quotation for ${displayName || product?.name}`,
      productName: displayName || product?.name || 'Machine',
      productCategory: displayCategory || product?.category || '',
      productCapacity: capacityText || product?.capacity || '',
      status: 'New',
      createdAt: new Date().toISOString()
    };

    try {
      await saveEnquiry(enquiryPayload);
      Swal.fire({
        icon: 'success',
        title: t('alertSuccessTitle'),
        text: t('alertSuccessText'),
        confirmButtonColor: '#04416d'
      });
      setQuoteForm({ name: '', phone: '', email: '', message: '' });
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      Swal.fire({
        icon: 'error',
        title: t('alertErrorTitle'),
        text: t('alertErrorText'),
        confirmButtonColor: '#04416d'
      });
    } finally {
      setSubmittingQuote(false);
    }
  };

  const scrollToConsultation = () => {
    const el = document.getElementById('pd-quick-quote-section');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="product-detail-page">
        <div className="loading-container" style={{ minHeight: '60vh' }}>
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-detail-page">
        <div className="container" style={{ padding: '120px 20px', textAlign: 'center' }}>
          <div className="not-found-card glass">
            <h2>{t('pdNotFoundTitle')}</h2>
            <p>{t('pdNotFoundDesc')}</p>
            <Link to="/products" className="btn btn-primary mt-4">
              <FaArrowLeft /> {t('pdBackToCatalog')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const defaultKeyFeatures = [
    t('featCustomSolutions'),
    t('featStainlessSteel'),
    t('featHeavyDuty'),
    t('featHighPerf'),
    t('featEnergySaving'),
    t('featLowMaint')
  ];

  const keyFeaturesList = defaultKeyFeatures;

  return (
    <div className="product-detail-page">
      {/* Integrated Breadcrumb & Top Bar */}
      <div className="pd-top-bar">
        <div className="container">
          <div className="pd-breadcrumb-wrapper">
            <nav className="pd-breadcrumbs" aria-label="Breadcrumb">
              <Link to="/"><FaHome /> {t('bcHome')}</Link>
              <FaChevronRight className="bc-sep" />
              <Link to="/products">{t('bcProducts')}</Link>
              {displayCategory && (
                <>
                  <FaChevronRight className="bc-sep" />
                  <span className="bc-category">{displayCategory}</span>
                </>
              )}
              <FaChevronRight className="bc-sep" />
              <span className="bc-current">{displayName}</span>
            </nav>
            <Link to="/products" className="pd-back-link">
              <FaArrowLeft /> {t('bcAllProducts')}
            </Link>
          </div>
        </div>
      </div>

      {/* Main Product Showcase Section */}
      <section className="pd-main-section">
        <div className="container">
          <div className="pd-showcase-grid">

            {/* Left Column: Image & Key Features */}
            <motion.div
              className="pd-media-column"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              {/* Product Image Card */}
              <div className="pd-image-card">
                <div
                  className="pd-main-image-viewport"
                  onClick={openLightbox}
                  title="Click for full view & zoom"
                >
                  <img
                    src={activeImage}
                    alt={displayName}
                    className="pd-main-img"
                  />
                  <div className="pd-zoom-hint">
                    <FaSearchPlus /> {t('zoomHint')}
                  </div>
                </div>

                {imagesList.length > 1 && (
                  <div className="pd-thumbnails-strip">
                    {imagesList.map((img, idx) => (
                      <button
                        key={idx}
                        type="button"
                        className={`pd-thumb-btn ${idx === selectedImgIndex ? 'active' : ''}`}
                        onClick={() => setSelectedImgIndex(idx)}
                      >
                        <img src={img} alt={`${product.name} view ${idx + 1}`} />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Key Features Card */}
              <div className="pd-key-features-card">
                <h3 className="pd-kf-title">{t('pdKeyFeatures')}</h3>
                <ul className="pd-kf-list">
                  {keyFeaturesList.map((feature, idx) => (
                    <li key={idx}>
                      <FaCheckCircle className="pd-kf-icon" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Right Column: Title, Badges, Overview, Detailed Specifications, Enquire Now */}
            <motion.div
              className="pd-details-column"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <div className="pd-info-card">

                {/* Product Title */}
                <h1 className="pd-product-title">{displayName}</h1>

                {/* Badges Row */}
                <div className="pd-badges-row">
                  {displayCategory && (
                    <span className="pd-badge-category-pill">
                      <FaIndustry /> {displayCategory}
                    </span>
                  )}
                  <span className="pd-badge-capacity-pill">
                    <FaTruck /> {capacityText}
                  </span>
                </div>

                <hr className="pd-divider" />

                {/* Product Overview */}
                <div className="pd-overview-block">
                  <h2 className="pd-subheading">{t('pdProductOverview')}</h2>
                  <p className="pd-overview-text">
                    {overviewContent}
                  </p>
                </div>

                {/* Detailed Specifications Box */}
                <div className="pd-specs-box">
                  <h3 className="pd-specs-box-title">{t('pdDetailedSpecs')}</h3>
                  <div className="pd-specs-box-content">
                    {detailedSpecs.map((paragraph, idx) => (
                      <p key={idx}>{paragraph}</p>
                    ))}
                  </div>
                </div>

                <hr className="pd-divider" />

                {/* Enquire Now & WhatsApp Action */}
                <div className="pd-enquire-action">
                  <button
                    type="button"
                    className="pd-btn-enquire"
                    onClick={scrollToConsultation}
                  >
                    {t('pdBtnEnquire')}
                  </button>

                  <a
                    href={getWhatsAppEnquiryUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="pd-btn-whatsapp"
                  >
                    <FaWhatsapp className="whatsapp-btn-icon" /> {t('pdBtnWhatsApp')}
                  </a>
                </div>

              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* In-Page Quick Consultation / Quotation Form */}
      <section className="pd-quote-section" id="pd-quick-quote-section">
        <div className="container">
          <div className="pd-quote-card glass">
            <div className="quote-header">
              <span className="quote-badge">{t('pdQuoteBadge')}</span>
              <h2>{t('pdQuoteTitle')}</h2>
              <p>
                {t('pdQuoteSpecificDesc', { name: displayName })}
              </p>
            </div>

            <form className="pd-quote-form" onSubmit={handleQuoteSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="quote-name">{t('formFullName')} *</label>
                  <input
                    id="quote-name"
                    type="text"
                    required
                    placeholder={t('formEnterFullName')}
                    value={quoteForm.name}
                    onChange={(e) => setQuoteForm({ ...quoteForm, name: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="quote-phone">{t('formPhone')} *</label>
                  <input
                    id="quote-phone"
                    type="tel"
                    required
                    placeholder={t('formPhonePlaceholder')}
                    value={quoteForm.phone}
                    onChange={(e) => setQuoteForm({ ...quoteForm, phone: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="quote-email">{t('formEmail')}</label>
                  <input
                    id="quote-email"
                    type="email"
                    placeholder={t('formEmailPlaceholder')}
                    value={quoteForm.email}
                    onChange={(e) => setQuoteForm({ ...quoteForm, email: e.target.value })}
                  />
                </div>

                <div className="form-group full-width">
                  <label htmlFor="quote-message">{t('formMessage')}</label>
                  <textarea
                    id="quote-message"
                    rows="3"
                    placeholder={t('formMessagePlaceholder')}
                    value={quoteForm.message}
                    onChange={(e) => setQuoteForm({ ...quoteForm, message: e.target.value })}
                  />
                </div>
              </div>

              <div className="quote-form-actions">
                <button
                  type="submit"
                  className="btn btn-primary btn-submit-quote"
                  disabled={submittingQuote}
                >
                  <FaPaperPlane /> {submittingQuote ? t('formSubmitting') : t('pdBtnEnquire')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Full View Lightbox Modal */}
      {isLightboxOpen && (
        <div className="pd-lightbox-overlay" onClick={closeLightbox}>
          <div className="pd-lightbox-container" onClick={(e) => e.stopPropagation()}>
            {/* Top Toolbar */}
            <div className="pd-lightbox-header">
              <div className="pd-lb-info">
                <span className="pd-lb-title">{displayName}</span>
                {imagesList.length > 1 && (
                  <span className="pd-lb-counter">
                    ({selectedImgIndex + 1} / {imagesList.length})
                  </span>
                )}
              </div>

              <div className="pd-lb-controls">
                <button
                  type="button"
                  className="pd-lb-btn"
                  onClick={handleZoomOut}
                  disabled={zoomLevel <= 1}
                  title="Zoom Out (-)"
                  aria-label="Zoom Out"
                >
                  <FaSearchMinus />
                </button>
                <span className="pd-lb-zoom-badge">
                  {Math.round(zoomLevel * 100)}%
                </span>
                <button
                  type="button"
                  className="pd-lb-btn"
                  onClick={handleZoomIn}
                  disabled={zoomLevel >= 3.5}
                  title="Zoom In (+)"
                  aria-label="Zoom In"
                >
                  <FaSearchPlus />
                </button>
                {zoomLevel > 1 && (
                  <button
                    type="button"
                    className="pd-lb-btn pd-lb-reset-btn"
                    onClick={handleResetZoom}
                    title="Reset Zoom (0)"
                    aria-label="Reset Zoom"
                  >
                    <FaRedo /> Reset
                  </button>
                )}
                <button
                  type="button"
                  className="pd-lb-btn pd-lb-close-btn"
                  onClick={closeLightbox}
                  title="Close (Esc)"
                  aria-label="Close"
                >
                  <FaTimes />
                </button>
              </div>
            </div>

            {/* Main Stage Viewport */}
            <div
              className={`pd-lightbox-viewport ${zoomLevel > 1 ? 'is-zoomed' : ''} ${isDragging ? 'is-dragging' : ''}`}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleMouseUp}
              onWheel={handleWheel}
            >
              <img
                src={activeImage}
                alt={`${displayName} Full View`}
                className="pd-lightbox-img"
                style={{
                  transform: `scale(${zoomLevel}) translate(${pan.x / zoomLevel}px, ${pan.y / zoomLevel}px)`,
                  transition: isDragging ? 'none' : 'transform 0.2s ease-out'
                }}
                draggable={false}
              />

              {/* Prev / Next navigation arrows if multiple images */}
              {imagesList.length > 1 && (
                <>
                  <button
                    type="button"
                    className="pd-lb-nav-btn prev"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImgIndex(prev => (prev - 1 + imagesList.length) % imagesList.length);
                      handleResetZoom();
                    }}
                    title="Previous Image"
                    aria-label="Previous"
                  >
                    <FaChevronLeft />
                  </button>
                  <button
                    type="button"
                    className="pd-lb-nav-btn next"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImgIndex(prev => (prev + 1) % imagesList.length);
                      handleResetZoom();
                    }}
                    title="Next Image"
                    aria-label="Next"
                  >
                    <FaChevronRight />
                  </button>
                </>
              )}
            </div>

            {/* Bottom Strip of Thumbnails (if multiple images) */}
            {imagesList.length > 1 && (
              <div className="pd-lightbox-thumbs">
                {imagesList.map((img, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className={`pd-lb-thumb-btn ${idx === selectedImgIndex ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedImgIndex(idx);
                      handleResetZoom();
                    }}
                  >
                    <img src={img} alt={`Thumbnail ${idx + 1}`} />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
